import { useCallback, useEffect, useRef, useState } from "react";
import { apiErrorMessage } from "@/api/client";

export interface InsightState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Re-run the request; keeps showing the previous data while it loads. */
  reload: () => void;
}

/**
 * Loads one insight payload. A newer request supersedes an older one, so
 * changing the range quickly never lets a slow earlier response overwrite it.
 */
export function useInsight<T>(load: () => Promise<T>, deps: readonly unknown[]): InsightState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const ticket = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loader = useCallback(load, deps);

  const run = useCallback(() => {
    const mine = ++ticket.current;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (mine !== ticket.current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (mine !== ticket.current) return;
        setError(apiErrorMessage(err));
      })
      .finally(() => {
        if (mine === ticket.current) setLoading(false);
      });
  }, [loader]);

  useEffect(() => {
    run();
    return () => {
      ticket.current += 1;
    };
  }, [run]);

  return { data, error, loading, reload: run };
}
